import type {
  BriefGap,
  BriefGapAction,
  BriefGapCategory,
} from '@travel-planner/shared/schemas';
import { Bed, Compass, MapPin, Route, Utensils, X } from 'lucide-react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './brief-gap-card.module.scss';

const styles = {
  root: pickCss(css, css.root),
  severityBar: pickCss(css, css.severityBar),
  body: pickCss(css, css.body),
  header: pickCss(css, css.header),
  badge: pickCss(css, css.badge),
  title: pickCss(css, css.title),
  catPill: pickCss(css, css.catPill),
  desc: pickCss(css, css.desc),
  actions: pickCss(css, css.actions),
  action: pickCss(css, css.action),
  dismiss: pickCss(css, css.dismiss),
};

const CATEGORY_META: Record<
  BriefGapCategory,
  { label: string; Icon: typeof Compass }
> = {
  brief: { label: 'BRIEF', Icon: Compass },
  transportation: { label: 'TRANSPORT', Icon: Route },
  accommodation: { label: 'STAY', Icon: Bed },
  activities: { label: 'ACTIVITIES', Icon: MapPin },
  dining: { label: 'FOOD', Icon: Utensils },
};

const SEVERITY_BADGE: Record<BriefGap['severity'], string> = {
  blocker: 'Blocker',
  warning: 'Warning',
  suggestion: 'Suggestion',
};

type BriefGapCardProps = {
  gap: BriefGap;
  onAction: (gap: BriefGap, action: BriefGapAction) => void;
};

/**
 * BriefGapCard — pen components `b3jzo` (blocker) / `Oxsep` (warning).
 * A single missing-info card: amber severity bar, severity badge, title,
 * a coloured category pill, the human description, and a single "Ask
 * client" primary button. A small × in the corner dismisses the card
 * (state-only, local to the conversation page).
 */
export const BriefGapCard = ({ gap, onAction }: BriefGapCardProps) => {
  const category = gap.category ?? 'brief';
  const { label, Icon } = CATEGORY_META[category];
  const canAsk = gap.actions.includes('ask-customer');
  const canDismiss = gap.actions.includes('generate-anyway');

  return (
    <article className={joinCss(styles.root({ severity: gap.severity }))}>
      <span aria-hidden="true" className={styles.severityBar()} />
      <div className={styles.body()}>
        <div className={styles.header()}>
          <span className={styles.badge()}>{SEVERITY_BADGE[gap.severity]}</span>
          <h4 className={styles.title()}>{gap.title}</h4>
          <span className={joinCss(styles.catPill({ category }))}>
            <Icon aria-hidden="true" size={10} />
            {label}
          </span>
          {canDismiss && (
            <button
              aria-label="Dismiss this gap"
              className={styles.dismiss()}
              onClick={() => onAction(gap, 'generate-anyway')}
              type="button"
            >
              <X aria-hidden="true" size={12} />
            </button>
          )}
        </div>
        <p className={styles.desc()}>{gap.description}</p>
        {canAsk && (
          <div className={styles.actions()}>
            <button
              className={styles.action()}
              onClick={() => onAction(gap, 'ask-customer')}
              type="button"
            >
              Ask client
            </button>
          </div>
        )}
      </div>
    </article>
  );
};
